import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeartbeat,
  faUsers,
  faCalendarAlt,
  faHeart,
  faSpinner,
  faSearch,
  faBuilding,
  faTimes,
  faCheck,
  faIdCard,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faInfoCircle, faStethoscope, faUserMd, faWheelchair, faAmbulance,
  faHospital, faSyringe, faPills, faBriefcaseMedical, faNotesMedical,
  faPrescriptionBottleAlt, faLungs, faBrain, faTooth, faBone,
  faVirus, faVial, faDna, faMicroscope, faXRay, faThermometerHalf,
  faLaptopMedical, faMobileAlt, faPhoneAlt, faClinicMedical,
  faCapsules, faLink, faSync, faClipboardList, faList
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import careProgramService from '../../../services/careProgramService';
import { getProgramTagStyle } from '../../../utils/careProgramUtils';
import PracticeSummaryModal from './PracticeSummaryModal';
import ConfirmDeleteModal from '../../../components/Common/ConfirmDeleteModal/ConfirmDeleteModal';
import EventLogSection from '../../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../../hooks/useEventLogModalScrollLock';
import { mergeServerPagination } from '../../../utils/paginationUtils';
import './GlobalDashboard.css';
import { toast } from 'react-toastify';

const PATIENT_RANGES = [
  { label: '0 patients', value: '0-0' },
  { label: '1-50 patients', value: '1-50' },
  { label: '51-100 patients', value: '51-100' },
  { label: '101-200 patients', value: '101-200' },
  { label: '201-500 patients', value: '201-500' },
  { label: '500+ patients', value: '501-99999' }
];

// Dynamic programs will be fetched in the component

const GlobalDashboard = () => {
  const navigate = useNavigate();
  const { clearBreadcrumbs, updateBreadcrumbs } = useBreadcrumb();
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = Number(user?.role_id) === 1;

  // State
  const [practices, setPractices] = useState([]);
  const [summary, setSummary] = useState({
    total_practices: 0,
    active_practices: 0,
    pending_practices: 0,
    locked_practices: 0,
    total_patients: 0,
    active_patients: 0,
    pending_patients: 0,
    locked_patients: 0,
    patients_enrolled_this_month: 0,
    rpm_patients: 0,
    ccm_patients: 0,
    pcm_patients: 0,
    rtm_patients: 0,
    cardiac_patients: 0
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  // Filter States
  const [statusFilter, setStatusFilter] = useState('');
  const [patientsFilter, setPatientsFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [carePrograms, setCarePrograms] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [programsModalOpen, setProgramsModalOpen] = useState(false);
  const [programsModalList, setProgramsModalList] = useState([]);
  const [programsModalPractice, setProgramsModalPractice] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [practiceToDelete, setPracticeToDelete] = useState(null);
  const [isDeletingPractice, setIsDeletingPractice] = useState(false);
  const [showLogSection, setShowLogSection] = useState(false);
  const logOverlayRef = React.useRef(null);
  const logContainerRef = React.useRef(null);
  useEventLogModalScrollLock(showLogSection, logOverlayRef, logContainerRef);

  const [isProgramDropdownOpen, setIsProgramDropdownOpen] = useState(false);
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc');
  const programDropdownRef = React.useRef(null);

  const selectedPrograms = programFilter ? programFilter.split(',').map(s => s.trim()).filter(Boolean) : [];

  const handleProgramToggle = (progId) => {
    let newPrograms;
    if (selectedPrograms.includes(progId)) {
      newPrograms = selectedPrograms.filter(id => id !== progId);
    } else {
      newPrograms = [...selectedPrograms, progId];
    }
    setProgramFilter(newPrograms.join(','));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (programDropdownRef.current && !programDropdownRef.current.contains(event.target)) {
        setIsProgramDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Breadcrumbs
  useEffect(() => {
    clearBreadcrumbs();
    return () => clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  // Fetch dynamic care programs for filtering
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await careProgramService.getCarePrograms();
        if (response.success) {
          setCarePrograms(response.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch care programs:', err);
      }
    };
    fetchPrograms();
  }, []);

  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch data — single effect with cancel flag to ignore stale responses when page size changes
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const page = pagination.current_page;
        const limit = pagination.per_page;
        const response = await api.get('/practices/dashboard-list', {
          params: {
            page,
            limit,
            search: searchTerm,
            status: statusFilter,
            patients_range: patientsFilter,
            program_type: programFilter,
            sortBy,
            sortOrder
          }
        });
        if (cancelled) return;

        if (response.data.success) {
          setPractices(response.data.data.practices);
          setSummary(response.data.data.summary);
          setPagination((prev) =>
            mergeServerPagination(response.data.data.pagination, {
              page,
              pageSize: limit
            })
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching dashboard data:', error);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    pagination.current_page,
    pagination.per_page,
    searchTerm,
    statusFilter,
    patientsFilter,
    programFilter,
    sortBy,
    sortOrder,
    refreshKey
  ]);

  useEffect(() => {
    const refresh = () => setRefreshKey((prev) => prev + 1);
    window.addEventListener('evitals:practice-patients-changed', refresh);
    return () => window.removeEventListener('evitals:practice-patients-changed', refresh);
  }, []);

  const fetchDashboardData = useCallback(async (page = pagination.current_page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    setRefreshKey((prev) => prev + 1);
  }, [pagination.current_page]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(localSearchTerm);
      setPagination((prev) => ({ ...prev, current_page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [localSearchTerm]);

  // Handlers
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  const handleSort = (column) => {
    const sortKey = column === 'idx' ? 'id' : column;
    const newOrder = sortBy === sortKey && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(sortKey);
    setSortOrder(newOrder);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleSearchChange = (e) => {
    setLocalSearchTerm(e.target.value);
  };

  const handleViewDetails = (id) => {
    navigate(`/dashboard/${id}?tab=dashboard`, { state: { fromDashboard: true } });
  };

  const handleEdit = (id) => {
    navigate(`/practices/add?editId=${id}`, { state: { fromDashboard: true } });
  };

  const handleSaveEdit = async () => {
    // This is no longer used in GlobalDashboard
  };

  const handleDelete = (practice) => {
    setPracticeToDelete(practice);
    setShowDeleteModal(true);
  };

  const cancelDeletePractice = () => {
    if (isDeletingPractice) return;
    setShowDeleteModal(false);
    setPracticeToDelete(null);
  };

  const confirmDeletePractice = async () => {
    if (!practiceToDelete) return;
    const deletedId = practiceToDelete.id;
    const deletedName = practiceToDelete.practice_name || 'Practice';
    setIsDeletingPractice(true);
    try {
      const response = await api.delete(`/practices/${deletedId}`);
      if (response.data.success) {
        toast.success(`${deletedName} was deleted successfully.`);
        setShowDeleteModal(false);
        setPracticeToDelete(null);
        // Optimistically remove deleted practice from local state immediately for instant UI response
        setPractices((prev) =>
          (prev || []).filter((p) => String(p.id) !== String(deletedId) && Number(p.id) !== Number(deletedId))
        );
        setSummary((prev) => ({
          ...prev,
          total_practices: Math.max(0, (prev?.total_practices || 0) - 1)
        }));
        // Re-fetch fresh dashboard data & summary counts from backend
        fetchDashboardData(pagination.current_page);
      } else {
        toast.error(response.data.message || 'Failed to delete practice');
      }
    } catch (error) {
      console.error('Error deleting practice:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete practice');
    } finally {
      setIsDeletingPractice(false);
    }
  };

  const getProgramTagClass = (name) => {
    if (!name) return '';
    return `tag-${String(name).toLowerCase().replace(/\s+/g, '-')}`;
  };

  const columns = useMemo(() => [
    {
      title: '#',
      key: 'id',
      sortable: true,
      className: 'reusable-table-centered-col col-index',
      render: (practice) => practice.id
    },
    {
      title: 'STATUS',
      key: 'status',
      sortable: true,
      className: 'reusable-table-centered-col col-status',
      render: (practice) => (
        <StatusBadge status={practice.status} className="dashboard-status-badge" />
      )
    },
    {
      title: 'PRACTICE',
      key: 'practice_name',
      sortable: true,
      className: 'col-practice',
      render: (practice) => (
        <span className="practice-name-text" onClick={() => handleViewDetails(practice.id)}>
          {practice.practice_name}
        </span>
      )
    },
    {
      title: 'TOTAL PATIENTS',
      key: 'total_patients',
      sortable: true,
      className: 'reusable-table-centered-col col-total',
      render: (practice) => (
        <div className="gd-patients-cell">
          <div className="gd-patients-inline">
            <span className="total-patients-number">{practice.total_patients}</span>
            <div className="gd-patient-badges">
              <span className="gd-patient-badge gd-patient-badge-a">A {practice.active_patients || 0}</span>
              <span className="gd-patient-badge gd-patient-badge-p">P {practice.pending_patients || 0}</span>
              <span className="gd-patient-badge gd-patient-badge-l">L {practice.locked_patients || 0}</span>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'PROGRAM ENROLLED',
      key: 'enrolled_programs',
      className: 'col-programs',
      render: (practice) => {
        const rawEnrolled = Array.isArray(practice.enrolled_programs) ? practice.enrolled_programs : [];
        const counts = practice.program_counts || {};

        const normalizeName = (item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') return (item.short_name || item.id || item.name || JSON.stringify(item));
          return String(item);
        };

        const getCount = (key) => {
          const strKey = String(key).toUpperCase();
          const matchKey = Object.keys(counts).find(k => k.toUpperCase() === strKey);
          const v = matchKey ? counts[matchKey] : counts[key];
          if (typeof v === 'number') return v;
          if (v && typeof v === 'object') {
            if (typeof v.count === 'number') return v.count;
            if (typeof v.total === 'number') return v.total;
          }
          return 0;
        };

        const programItems = rawEnrolled.length > 0
          ? rawEnrolled.map((p) => {
            const name = normalizeName(p);
            return {
              name,
              count: getCount(name),
              style: typeof p === 'object' ? getProgramTagStyle(p) : null
            };
          })
          : Object.keys(counts).map((k) => ({ name: k, count: getCount(k), style: null }));

        const visible = programItems.slice(0, 4);
        const remaining = Math.max(0, programItems.length - visible.length);

        return (
          <div className="program-tags-container">
            {programItems.length > 0 ? (
              <>
                {visible.map((prog) => (
                  <span
                    key={prog.name}
                    className={`program-tag ${prog.style ? '' : getProgramTagClass(prog.name)}`}
                    style={prog.style || undefined}
                  >
                    {prog.name} {prog.count !== undefined ? `(${prog.count})` : ''}
                  </span>
                ))}
                {remaining > 0 && (
                  <span
                    className="program-tag program-tag-more"
                    onClick={() => { setProgramsModalList(programItems); setProgramsModalPractice(practice); setProgramsModalOpen(true); }}
                    role="button"
                    tabIndex={0}
                  >
                    +{remaining}
                  </span>
                )}
              </>
            ) : (
              <span className="program-tag program-tag-none">None</span>
            )}
          </div>
        );
      }
    },
    {
      title: 'ACTION',
      key: 'actions',
      className: 'reusable-table-centered-col col-actions',
      render: (practice) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <ActionButtons
            onView={() => handleViewDetails(practice.id)}
            onEdit={isSuperAdmin ? () => handleEdit(practice.id) : null}
            onDelete={isSuperAdmin ? () => handleDelete(practice) : null}
            hideEdit={!isSuperAdmin}
            hideDelete={!isSuperAdmin}
          />
        </div>
      )
    }
  ], [isSuperAdmin]);

  const totalPrograms = typeof summary.total_programs === 'number' ? summary.total_programs : carePrograms.length;
  const programsModalTotalPatients = programsModalList.reduce((sum, item) => sum + (Number(item.count) || 0), 0);

  const programAvailabilityLabel = useMemo(() => {
    const names = (carePrograms || [])
      .map((program) => program.short_name || program.full_name)
      .filter(Boolean);
    if (names.length === 0) return 'Programs available';
    if (names.length === 1) return `${names[0]} available`;
    if (names.length === 2) return `${names[0]} & ${names[1]} available`;
    return `${names.slice(0, 2).join(', ')} & more available`;
  }, [carePrograms]);

  const formatSummaryNumber = (value) => Number(value || 0).toLocaleString();

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <h2 className="dashboard-title">Practice List</h2>
      </div>
      {/* Summary Section */}
      <div className="enrollment-summary-section">
        <div className="enrollment-summary-header">
          <div className="enrollment-summary-header-left">
            <div>
              <h3 className="enrollment-summary-title">Enrollment Summary</h3>
            </div>
          </div>
        </div>

        <div className="enrollment-summary-grid">
          <div className="enrollment-stat-box">
            <div className="enrollment-stat-top">
              <div>
                <p className="enrollment-stat-label">Total Practices</p>
                <p className="enrollment-stat-value">{formatSummaryNumber(summary.total_practices)}</p>
              </div>
              <div className="enrollment-stat-icon">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
            </div>
            <div className="enrollment-stat-badges">
              <span className="enrollment-stat-badge enrollment-stat-badge-a">A {formatSummaryNumber(summary.active_practices)}</span>
              <span className="enrollment-stat-badge enrollment-stat-badge-p">P {formatSummaryNumber(summary.pending_practices)}</span>
              <span className="enrollment-stat-badge enrollment-stat-badge-l">L {formatSummaryNumber(summary.locked_practices)}</span>
            </div>
          </div>

          <div className="enrollment-stat-box">
            <div className="enrollment-stat-top">
              <div>
                <p className="enrollment-stat-label">Total Patients</p>
                <p className="enrollment-stat-value">{formatSummaryNumber(summary.total_patients)}</p>
              </div>
              <div className="enrollment-stat-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
            </div>
            <div className="enrollment-stat-badges">
              <span className="enrollment-stat-badge enrollment-stat-badge-a">A {formatSummaryNumber(summary.active_patients)}</span>
              <span className="enrollment-stat-badge enrollment-stat-badge-p">P {formatSummaryNumber(summary.pending_patients)}</span>
              <span className="enrollment-stat-badge enrollment-stat-badge-l">L {formatSummaryNumber(summary.locked_patients)}</span>
            </div>
          </div>

          <div className="enrollment-stat-box">
            <div className="enrollment-stat-top">
              <div>
                <p className="enrollment-stat-label">Total Programs</p>
                <p className="enrollment-stat-value">{formatSummaryNumber(totalPrograms)}</p>
              </div>
              <div className="enrollment-stat-icon">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
            </div>
            <div className="enrollment-stat-subline">{programAvailabilityLabel}</div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="dashboard-filters">
        <div className="search-box-wrap">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search practices..."
            value={localSearchTerm}
            onChange={handleSearchChange}
          />
        </div>

        <select
          className="dashboard-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination(prev => ({ ...prev, current_page: 1 }));
          }}
        >
          <option value="">Status</option>
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Locked">Locked</option>
        </select>

        <select
          className="dashboard-select"
          value={patientsFilter}
          onChange={(e) => {
            setPatientsFilter(e.target.value);
            setPagination(prev => ({ ...prev, current_page: 1 }));
          }}
        >
          <option value="">Patient Range</option>
          {PATIENT_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>

        <div className="program-filter-wrap" ref={programDropdownRef}>
          <div
            className="dashboard-select program-filter-trigger"
            onClick={() => setIsProgramDropdownOpen(!isProgramDropdownOpen)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsProgramDropdownOpen(!isProgramDropdownOpen);
              }
            }}
          >
            <span>
              {selectedPrograms.length === 0 ? 'Programs' : `${selectedPrograms.length} Program(s)`}
            </span>
          </div>
          {isProgramDropdownOpen && (
            <div className="program-filter-dropdown">
              <label className="program-filter-option">
                <input
                  type="checkbox"
                  checked={selectedPrograms.length === 0}
                  onChange={() => {
                    setProgramFilter('');
                    setPagination(prev => ({ ...prev, current_page: 1 }));
                  }}
                />
                <span className={selectedPrograms.length === 0 ? 'active' : ''}>All Programs</span>
              </label>
              {carePrograms.map(prog => (
                <label key={prog.id} className="program-filter-option">
                  <input
                    type="checkbox"
                    checked={selectedPrograms.includes(prog.short_name)}
                    onChange={() => handleProgramToggle(prog.short_name)}
                  />
                  <span className={selectedPrograms.includes(prog.short_name) ? 'active' : ''}>{prog.short_name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className="dashboard-clear-all"
        onClick={() => {
          setSearchTerm('');
          setLocalSearchTerm('');
          setStatusFilter('');
          setPatientsFilter('');
          setProgramFilter('');
          setSortBy('status');
          setSortOrder('asc');
          setPagination(prev => ({ ...prev, current_page: 1 }));
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSearchTerm('');
            setLocalSearchTerm('');
            setStatusFilter('');
            setPatientsFilter('');
            setProgramFilter('');
            setSortBy('status');
            setSortOrder('asc');
            setPagination(prev => ({ ...prev, current_page: 1 }));
          }
        }}
        title="Clear all filters and search criteria"
      >
        Clear All
      </div>

      {programsModalOpen && (
        <div className="dashboard-modal-overlay">
          <div className="dashboard-modal-content dashboard-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Enrolled Programs for {programsModalPractice?.practice_name}</h3>
              </div>
              <button type="button" className="close-btn" onClick={() => setProgramsModalOpen(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-subtitle-row">
                <span className="modal-subtitle-label">{programsModalList.length} program{programsModalList.length === 1 ? '' : 's'} enrolled</span>
                <span className="modal-subtitle-label">{programsModalTotalPatients} patient{programsModalTotalPatients === 1 ? '' : 's'}</span>
              </div>
              {programsModalList.length > 0 ? (
                programsModalList.map(p => (
                  <div key={p.name} className="modal-list-item">
                    <span className={`program-tag ${getProgramTagClass(p.name)} modal-program-tag`}>
                      {p.name}
                    </span>
                    <span className="modal-item-count">{p.count ? `${p.count} patient${p.count === 1 ? '' : 's'}` : '0 patients'}</span>
                  </div>
                ))
              ) : (
                <div className="modal-empty-state">No enrolled programs found.</div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setProgramsModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}


      {/* Table Section */}
      <div className="dashboard-table-card">
        {isLoading ? (
          <div className="loading-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="mb-3" />
            <p>Loading Dashboard Data...</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={practices}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              isPagination={false}
            />
            <ListScreenPaginationBar
              entriesValue={pagination.per_page === 9999 ? 'all' : String(pagination.per_page)}
              onEntriesChange={(val) => setPagination(prev => ({
                ...prev,
                current_page: 1,
                per_page: val === 'all' ? 9999 : parseInt(val, 10)
              }))}
              currentPage={pagination.current_page}
              totalRecords={pagination.total}
              totalPages={pagination.total_pages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      {isSuperAdmin && (
        <div className="dashboard-event-log-actions">
          <button
            type="button"
            className="pm-events-log-btn"
            onClick={() => setShowLogSection(true)}
          >
            <FontAwesomeIcon icon={faList} />
            Event Logs
          </button>
        </div>
      )}

      <PracticeSummaryModal
        show={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        summary={summary}
        practices={practices}
      />

      {showLogSection && (
        <div ref={logOverlayRef} className="dashboard-modal-overlay">
          <div
            ref={logContainerRef}
            className="dashboard-modal-content dashboard-event-logs-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Event Logs</h3>
              <button type="button" className="close-btn" onClick={() => setShowLogSection(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <EventLogSection moduleId="Practice" />
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        show={showDeleteModal}
        onCancel={cancelDeletePractice}
        onConfirm={confirmDeletePractice}
        isDeleting={isDeletingPractice}
        message={
          <p>
            Are you sure you want to delete the practice{' '}
            <strong>{practiceToDelete?.practice_name}</strong>?
          </p>
        }
      />
    </div>
  );
};

export default GlobalDashboard;
